import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, of, catchError } from 'rxjs';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { SessionStorageService } from '../../services/SessionStorageService';
import { TokenService } from '../../services/TokenService';
import { IUserRepo, OrgPublicInfo, ProfileData, ProfilePayload, SignupOutcome, SignupPayload } from '../interfaces/iuser.repo';
import { UrlPath } from '../../services/UrlPath';
import { Organization, User } from '@socrates/common';

@Injectable()
export class UserRepo extends IUserRepo {
	private _user: User;

	constructor(
		private http: HttpClient,
		private urlPath: UrlPath,
		private sessionStorageService: SessionStorageService,
		private tokenService: TokenService,
	) {
		super();
	}

	logout(): void {
		const path = this.urlPath.getAuthPath('api/auth/logout');
		this.http.get(path, { withCredentials: true });
		this.tokenService.clearTokens();
		this.sessionStorageService.clearData('user');
	}

	async loginUser(userCredentials: any): Promise<User> {
		const path = this.urlPath.getUnauthPath('login');
		const response: any = await firstValueFrom(
			this.http.post(path, userCredentials, { responseType: 'json', withCredentials: true })
		);
		if (!response) throw new Error('Invalid user');

		this.tokenService.setTokens({
			idToken: response.idToken,
			accessToken: response.accessToken,
			expiresAt: response.expiresAt,
		});
		this._user = new User(response.user);
		if (response.user.organization) {
			this._user.organization = new Organization(response.user.organization);
		} else if (response.user._organization) {
			this._user.organization = new Organization(response.user._organization);
		}
		response.user.organization = instanceToPlain(this._user.organization);
		this.sessionStorageService.setData('user', response.user);
		return this._user;
	}

	async updateOwnPassword(password: string): Promise<boolean> {
		const path = this.urlPath.getAuthPath('updateOwnPassword');
		const response: any = await firstValueFrom(
			this.http.post(path, { password }, { responseType: 'json' })
		);
		if (!response?.success) throw new Error('Unsuccessful.');
		return true;
	}

	async updateOwnUser(user: User): Promise<User> {
		const path = this.urlPath.getAuthPath('updateOwnUser');
		await firstValueFrom(
			this.http.post(path, instanceToPlain(user, { excludePrefixes: ['_'] }), { responseType: 'json' })
		);
		this._user = new User(user);
		return this._user;
	}

	/** Validates session locally. Triggers a token refresh if needed.
	 *  On page reload, tokens are gone but httpOnly cookie may still be valid. */
	async validateSession(): Promise<User> {
		if (this._user && !this.tokenService.isExpiringSoon()) {
			return this._user;
		}

		// Either no tokens (page reload) or expiring soon — attempt refresh via httpOnly cookie
		await this.tokenService.refresh();
		if (!this._user) {
			this._user = this.userFromSession();
		}
		if (!this._user) throw new Error('No session');
		return this._user;
	}

	async firstLoginResetPassword(username: string, password: string): Promise<User> {
		const path = this.urlPath.getUnauthPath('setFirstLogin');
		const response: any = await firstValueFrom(
			this.http.post(path, { username, password }, { responseType: 'json', withCredentials: true })
		);
		if (!response?.success) throw new Error('Invalid updated');

		this.tokenService.setTokens({
			idToken: response.idToken,
			accessToken: response.accessToken,
			expiresAt: response.expiresAt,
		});
		this._user = new User(response.user);
		this.sessionStorageService.setData('user', response.user);
		return this._user;
	}

	async passwordResetConfirm(username: string, newPassword: string, passwordCode: string): Promise<User> {
		const path = this.urlPath.getUnauthPath('forgotPasswordConfirm');
		const response: any = await firstValueFrom(
			this.http.post(path, { username, newPassword, passwordCode }, { responseType: 'json', withCredentials: true })
		);
		if (!response?.idToken) throw new Error('Password reset unsuccessful.');

		this.tokenService.setTokens({
			idToken: response.idToken,
			accessToken: response.accessToken,
			expiresAt: response.expiresAt,
		});
		this._user = new User(response.user);
		this.sessionStorageService.setData('user', response.user);
		return this._user;
	}

	async requestPasswordReset(username: string): Promise<boolean> {
		const path = this.urlPath.getUnauthPath('forgotPassword');
		const response: any = await firstValueFrom(
			this.http.post(path, { username }, { responseType: 'json' })
		);
		if (!response?.success) throw new Error('Request password reset unsuccessful.');
		return true;
	}

	async listPublicOrgs(): Promise<OrgPublicInfo[]> {
		const path = this.urlPath.getUnauthPath('orgs');
		return firstValueFrom(this.http.get<OrgPublicInfo[]>(path));
	}

	async signup(payload: SignupPayload): Promise<SignupOutcome> {
		const path = this.urlPath.getUnauthPath('signup');
		return firstValueFrom(this.http.post<SignupOutcome>(path, payload, { responseType: 'json' }));
	}

	async resolveInvite(token: string): Promise<{ orgId: string; orgName: string }> {
		const path = this.urlPath.getUnauthPath(`invite/${token}`);
		return firstValueFrom(this.http.get<{ orgId: string; orgName: string }>(path));
	}

	async getOwnProfile(): Promise<ProfileData> {
		const path = this.urlPath.getAuthPath('ownProfile');
		return firstValueFrom(this.http.get<ProfileData>(path));
	}

	async updateProfile(payload: ProfilePayload): Promise<void> {
		const path = this.urlPath.getAuthPath('updateOwnUser');
		await firstValueFrom(this.http.post(path, payload, { responseType: 'json' }));
	}

	async getMyAccount(): Promise<{ trustLevel: number; accountState: string }> {
		const path = this.urlPath.getOrgPath('myAccount');
		return firstValueFrom(
			this.http.get<{ trustLevel: number; accountState: string }>(path).pipe(
				catchError(() => of({ trustLevel: 0, accountState: 'Active' }))
			)
		);
	}

	/** Reconstructs the User from non-sensitive profile data in sessionStorage.
	 *  On page reload, tokens are in-memory only — caller must trigger /refresh
	 *  separately to obtain fresh tokens before making authenticated requests. */
	userFromSession(): User {
		const userData: any = this.sessionStorageService.getData('user');
		if (!userData) return null;

		this._user = new User(userData);
		if (userData.organization) {
			this._user.organization = plainToInstance(Organization, userData.organization);
		}
		return this._user;
	}
}
